<template>
  <div class="filters-wrapper">
    <v-autocomplete
      :value="selectedFilters.mediumIds"
      :items="sortedMedia"
      placeholder="Media"
      v-bind="props.multiselect"
      item-value="id"
      item-text="name"
      class="mr-2 width-220"
      @input="selectToggleFilter({ mediumIds: $event })"
    >
      <template #selection="{ item, index }">
        <AppMultiselectChoices
          :index="index"
          :array-length="selectedFilters.mediumIds.length"
          :label="item.name"
        />
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-if="!archive"
      :value="selectedFilters.statuses"
      :items="filtersOptions.projectStatuses"
      placeholder="Status"
      item-text="status"
      item-value="status"
      v-bind="props.multiselect"
      class="mr-2 width-220"
      @input="selectToggleFilter({ statuses: $event })"
    >
      <template #selection="{ item, index }">
        <AppMultiselectChoices
          :index="index"
          :array-length="selectedFilters.statuses.length"
          :label="item.status"
        />
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-if="!archive && departmentFilterPermitted"
      :value="selectedFilters.departmentIds"
      :items="filtersOptions.departments"
      item-value="id"
      item-text="name"
      placeholder="Oddział"
      v-bind="props.multiselect"
      class="mr-2 width-220"
      @input="selectToggleFilter({ departmentIds: $event })"
    >
      <template #selection="{ item, index }">
        <AppMultiselectChoices
          :index="index"
          :array-length="selectedFilters.departmentIds.length"
          :label="item.shortcut"
        />
      </template>
    </v-autocomplete>

    <v-autocomplete
      :value="selectedFilters.tagIds"
      :items="sortedTags"
      placeholder="Tagi"
      v-bind="props.multiselect"
      item-value="id"
      item-text="name"
      class="mr-2 width-220"
      @input="selectToggleFilter({ tagIds: $event })"
    >
      <template #selection="{ item, index }">
        <AppMultiselectChoices
          :index="index"
          :array-length="selectedFilters.tagIds.length"
          :label="item.name"
        />
      </template>
    </v-autocomplete>

    <v-autocomplete
      :value="selectedFilters.projectMembers"
      :items="sortedUsers"
      item-value="id"
      :item-text="(item) => getUserName(item)"
      placeholder="Użytkownik z. projektowy"
      v-bind="props.multiselect"
      class="multiselect-field mr-2 width-220"
      dense
      @change="filterElements({ projectMembers: $event })"
    >
      <template #selection="{ item, index }">
        <v-chip
          v-if="index < 2"
          small
          :color="item | fullName | avatarColor"
        >
          <span class="white--text">{{ item | memberInitials }}</span>
        </v-chip>
        <span
          v-if="index === 2"
          class="light-grey--text caption"
        >(+{{ selectedFilters.projectMembers.length - 2 }})</span>
      </template>
    </v-autocomplete>
    <v-autocomplete
      :value="selectedFilters.projectManagers"
      :items="sortedUsers"
      item-value="id"
      :item-text="(item) => getUserName(item)"
      placeholder="Użytkownik z. decyzyjny"
      v-bind="props.multiselect"
      class="multiselect-field mr-2 width-220"
      dense
      @change="filterElements({ projectManagers: $event })"
    >
      <template #selection="{ item, index }">
        <v-chip
          v-if="index < 2"
          small
          :color="item | fullName | avatarColor"
        >
          <span class="white--text">{{ item | memberInitials }}</span>
        </v-chip>
        <span
          v-if="index === 2"
          class="light-grey--text caption"
        >(+{{ selectedFilters.projectManagers.length - 2 }})</span>
      </template>
    </v-autocomplete>
    <v-autocomplete
      :value="selectedFilters.projectOrderers"
      :items="sortedUsers"
      item-value="id"
      :item-text="(item) => getUserName(item)"
      placeholder="Użytkownik z. zlecający"
      v-bind="props.multiselect"
      class="multiselect-field mr-2 width-220"
      dense
      @change="filterElements({ projectOrderers: $event })"
    >
      <template #selection="{ item, index }">
        <v-chip
          v-if="index < 2"
          small
          :color="item | fullName | avatarColor"
        >
          <span class="white--text">{{ item | memberInitials }}</span>
        </v-chip>
        <span
          v-if="index === 2"
          class="light-grey--text caption"
        >(+{{ selectedFilters.projectOrderers.length - 2 }})</span>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-if="!defaultUsersFilter"
      :value="selectedFilters.users"
      :items="sortedUsers"
      item-value="id"
      :item-text="(item) => getUserName(item)"
      placeholder="Użytkownik"
      v-bind="props.multiselect"
      class="multiselect-field mr-2 width-220"
      dense
      @change="filterElements({ users: $event })"
    >
      <template #selection="{ item, index }">
        <v-chip
          v-if="index < 2"
          small
          :color="item | fullName | avatarColor"
        >
          <span class="white--text">{{ item | memberInitials }}</span>
        </v-chip>
        <span
          v-if="index === 2"
          class="light-grey--text caption"
        >(+{{ selectedFilters.users.length - 2 }})</span>
      </template>
    </v-autocomplete>

    <v-autocomplete
      :value="selectedFilters.campaignIds"
      :items="sortedCampaigns"
      placeholder="Kampanie"
      v-bind="props.multiselect"
      item-value="id"
      item-text="name"
      class="mr-2 width-220"
      @input="selectToggleFilter({ campaignIds: $event })"
    >
      <template #selection="{ item, index }">
        <AppMultiselectChoices
          :index="index"
          :array-length="selectedFilters.campaignIds.length"
          :label="item.name"
        />
      </template>
    </v-autocomplete>

    <v-autocomplete
      :value="selectedFilters.projectTypes"
      :items="filtersOptions.projectTypes"
      placeholder="Typ projektu"
      v-bind="props.multiselect"
      class="mr-2 width-220"
      @input="selectToggleFilter({ projectTypes: $event })"
    >
      <template #selection="{ item, index }">
        <AppMultiselectChoices
          :index="index"
          :array-length="selectedFilters.projectTypes.length"
          :label="item"
        />
      </template>
    </v-autocomplete>

    <v-btn
      v-if="filtersActive"
      text
      class="primary--text mr-2"
      @click="clearAllFilters"
    >
      Wyczyść filtry
    </v-btn>
  </div>
</template>

<script>
import AppMultiselectChoices from '@/components/UI/AppMultiselectChoices.vue';
import utils from '@/helpers/utils';
import props from '@/helpers/componentsProps';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  props: {
    archive: {
      type: Boolean,
      default: false,
    },
    defaultUsersFilter: {
      type: [Boolean, Number],
      default: false,
    },
  },

  components: {
    AppMultiselectChoices,
  },

  data() {
    return {
      props,
    };
  },

  computed: {
    ...mapState(['isLoading']),

    ...mapState('filters', ['selectedFilters', 'filtersOptions']),

    ...mapGetters('filters', ['sortedOptions']),

    ...mapGetters('auth', ['checkUserPermissions','checkBelongsToDko']),

    sortedUsers() {
      return this.sortedOptions('users');
    },

    sortedMedia() {
      return this.sortedOptions('media', 'mediumIds');
    },

    sortedTags() {
      return this.sortedOptions('tags', 'tagIds');
    },
    sortedCampaigns() {
      return this.sortedOptions('campaigns', 'campaignIds');
    },

    filtersActive() {
      let offset = 0;
      if (this.archive) offset += 1;
      if (this.defaultUsersFilter) offset += 1;
      return utils.areFiltersSet(this.selectedFilters, offset);
    },

    departmentFilterPermitted() {
      return this.checkBelongsToDko;
    },
  },

  methods: {
    ...mapActions('filters', ['setFilters', 'clearFilters']),

    selectToggleFilter(filterObject) {
      utils.checkIfFilterExists(filterObject, this.selectedFilters, this.filterElements);
    },

    filterElements(filterObject) {
      this.setFilters(filterObject).then(() => {
        this.$emit('search');
      });
    },

    clearAllFilters() {
      this.date = [];
      const defaultParams = {};
      if (this.archive) defaultParams.statuses = ['Zaakceptowany i zarchiwizowany'];
      if (this.defaultUsersFilter) defaultParams.users = [this.defaultUsersFilter];

      this.clearFilters(defaultParams).then(() => {
        this.$emit('search');
      });
    },

    getUserName(user) {
      return utils.getFullName(user);
    },
  },
};
</script>

<style lang="scss" scoped>
.multiselect-field::v-deep .v-chip {
  width: 24px;
  font-weight: 500;
  justify-content: center;
  padding: 0;
  margin: 0 2px !important;
}
.filters-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end-reversed;
  flex-wrap: wrap;
  flex-direction: row-reverse;
  position: relative;
  max-width: 920px;
  height: 100%;
}
</style>
